import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Image,
  Text,
  VStack,
  useColorModeValue,
  Container,
  Heading,
  Flex,
  RadioGroup,
  Radio,
  Stack,
  Divider,
} from "@chakra-ui/react";
import { productStructure } from "../../services/productStructure";

function ProductSelection({ category, onSelect }) {
  const [selectedRange, setSelectedRange] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedBrand, setSelectedBrand] = useState('Calibrite');
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const selectedBg = useColorModeValue("green.50", "green.800");
  const selectedBorder = useColorModeValue("green.500", "green.500");

  // Get available brands for the current category and products
  const availableBrands = useMemo(() => {
    const items = selectedRange?.subProducts || productStructure[category.type];
    // Only get brands if we're at the subProduct level or if the items don't have subProducts
    if (items?.length && (!items[0].subProducts || selectedRange)) {
      const brands = new Set(items.map(product => product.brand));
      return Array.from(brands);
    }
    return [];
  }, [category.type, selectedRange]);

  // Get the products to display
  const products = useMemo(() => {
    const items = selectedRange?.subProducts || productStructure[category.type];
    if (!items) return [];
    
    // If items have subProducts and we're not in a range, show all items
    if (!selectedRange && items[0]?.subProducts) {
      return items;
    }
    
    // Otherwise filter by brand
    return items.filter(product => product.brand === selectedBrand);
  }, [category.type, selectedRange, selectedBrand]);

  const handleRangeSelect = (product) => {
    if (product.subProducts) {
      setSelectedRange(product);
    } else {
      setSelectedProduct(product);
      onSelect({ ...product, type: category.type });
    }
  };

  const handleSubProductSelect = (subProduct) => {
    setSelectedProduct(subProduct);
    onSelect({ ...subProduct, type: category.type });
  };

  const handleBrandChange = (value) => {
    setSelectedBrand(value);
    setSelectedProduct(null);
  };

  const renderProductGrid = (items, onItemSelect) => (
    <Box
      display="flex"
      flexWrap={{ base: "wrap", xl: "nowrap" }}
      gap={{ base: 4, md: 6 }}
      justifyContent="center"
      w="100%"
    >
      {items.map((item) => (
        <Box
          key={item.id}
          borderWidth="2px"
          borderRadius="lg"
          borderColor={
            selectedProduct?.id === item.id || selectedRange?.id === item.id
              ? selectedBorder
              : borderColor
          }
          p={{ base: 2, sm: 3, md: 4 }}
          cursor="pointer"
          transition="all 0.2s"
          bg={
            selectedProduct?.id === item.id || selectedRange?.id === item.id
              ? selectedBg
              : "transparent"
          }
          onClick={() => onItemSelect(item)}
          position="relative"
          transformOrigin="center center"
          w={{
            base: "calc(50% - 8px)",
            md: "calc(33.333% - 16px)",
            xl: "200px",
          }}
          minW={{ base: "150px", md: "200px" }}
          maxW={{ base: "none", xl: "250px" }}
          flexShrink={1}
          flexGrow={{ base: 1, xl: 0 }}
          _hover={{
            borderColor: selectedBorder,
            transform: "scale(1.02)",
            boxShadow: "lg",
            zIndex: 1,
          }}
        >
          <VStack spacing={4} align="stretch">
            <Box
              width="100%"
              height="200px"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Image
                src={item.image}
                alt={item.name}
                maxH="100%"
                width="auto"
                objectFit="contain"
                fallbackSrc="https://via.placeholder.com/450"
              />
            </Box>
            <Box>
              <Text
                fontSize="md"
                fontWeight="medium"
                textAlign="center"
                noOfLines={2}
              >
                {item.name}
              </Text>
              {item.description && (
                <Text fontSize="sm" color="gray.500" textAlign="center" mt={1}>
                  {item.description}
                </Text>
              )}
              {!item.subProducts && (
                <Text fontSize="sm" color="gray.500" textAlign="center" mt={1}>
                  {item.id}
                </Text>
              )}
            </Box>
          </VStack>
        </Box>
      ))}
    </Box>
  );

  const renderBrandSelection = () => (
    <Box py={4}>
      <Divider mb={4} />
      <RadioGroup onChange={handleBrandChange} value={selectedBrand}>
        <Stack direction="row" spacing={8} justify="center">
          {availableBrands.map((brand) => (
            <Radio key={brand} value={brand} size="lg">
              {brand}
            </Radio>
          ))}
        </Stack>
      </RadioGroup>
    </Box>
  );

  return (
    <Box
      py={4}
      h={{ base: "auto", md: "100vh" }}
      maxH={{ base: "none", md: "800px" }}
      display="flex"
      flexDirection="column"
    >
      <Container
        maxW="container.xl"
        h="100%"
        display="flex"
        flexDirection="column"
      >
        <Flex align="center" mb={{ base: 4, md: 8 }} flexShrink={0}>
          <Heading size="lg" textAlign="center" flex="1">
            {selectedRange
              ? `Select ${selectedRange.name} Product`
              : `Select Your ${category.type} Product`}
          </Heading>
        </Flex>
        <Box
          overflow="auto"
          flex="1"
          minH={0}
          display="flex"
          flexDirection="column"
          alignItems="center"
          mx={-2}
        >
          <Flex
            minW="min-content"
            minH="min-content"
            gap={{ base: 2, sm: 4, md: 6 }}
            justify="center"
            align="center"
            mx="auto"
            w="calc(100% - 8px)"
            h="100%"
            p={2}
          >
            {renderProductGrid(products, selectedRange ? handleSubProductSelect : handleRangeSelect)}
          </Flex>

          {availableBrands.length > 1 && renderBrandSelection()}
        </Box>
      </Container>
    </Box>
  );
}

ProductSelection.propTypes = {
  category: PropTypes.shape({
    type: PropTypes.string.isRequired,
    title: PropTypes.string,
  }).isRequired,
  onSelect: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
};

export default ProductSelection;
